import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Link, Stack } from '@mui/material';

import { MplInformationMessage } from '@mopla/ui';

import { IZone } from '../types';
import { isWholeNetworkSelection } from '../zones';

import { ZoneTile } from './ZoneTile';

interface IZonesContentProps {
	zones: IZone[];
	selectedZoneIds: string[];
	onToggleZone: (zoneId: string) => void;
	zoneMapUrl?: string;
}

// Tile text area in a 2-col layout at 343px container:
//   (343 - 8 gap) / 2 = 167.5px tile
//   minus 12px left pad, 32px badge, 8px gap, 12px right pad = ~103px
const TILE_TEXT_WIDTH_PX = 103;
const TILE_NAME_FONT = '600 16px "Public Sans", sans-serif';

const measureTextWidth = (text: string, font: string): number => {
	if (typeof document === 'undefined') return 0;
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');
	if (!ctx) return 0;
	ctx.font = font;
	return ctx.measureText(text).width;
};

export const ZonesContent: FC<IZonesContentProps> = ({
	zones,
	selectedZoneIds,
	onToggleZone,
	zoneMapUrl,
}) => {
	const { t } = useTranslation('ticketshop');

	const isWholeNetwork = isWholeNetworkSelection(zones, selectedZoneIds);

	// Switch to full-width single column when any zone name overflows the
	// 2-col tile text area at the rendered font.
	const useSingleColumn = useMemo(
		() =>
			zones.some(
				(z) => measureTextWidth(z.name, TILE_NAME_FONT) > TILE_TEXT_WIDTH_PX
			),
		[zones]
	);

	return (
		<Stack gap={1} sx={{ width: '100%' }}>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: useSingleColumn ? '1fr' : 'repeat(2, 1fr)',
					gap: 1,
					width: '100%',
					pb: 2,
				}}
			>
				{zones.map((zone) => {
					const selected = selectedZoneIds.includes(zone.id);
					const included = !selected && isWholeNetwork;
					return (
						<ZoneTile
							key={zone.id}
							code={zone.zoneCode}
							name={zone.name}
							isSelected={selected}
							isIncluded={included}
							onClick={() => onToggleZone(zone.id)}
						/>
					);
				})}
			</Box>

			{zoneMapUrl && (
				<Link
					href={zoneMapUrl}
					target="_blank"
					rel="noopener noreferrer"
					underline="always"
					sx={{
						color: (theme) => theme.palette.primary.main,
						textDecorationColor: (theme) => theme.palette.primary.main,
						typography: 'l1',
						alignSelf: 'flex-start',
					}}
				>
					{t('ticket_detail.zones_view_map')}
				</Link>
			)}

			<MplInformationMessage>
				{t('ticket_detail.zones_whole_network_info')}
			</MplInformationMessage>
		</Stack>
	);
};
